import * as React from 'react'
import {unreachableCase} from '@kapitalist/utils/exhaustive'
import * as M from '@kapitalist/utils/Map'
import {recordKeySet} from '@kapitalist/utils/Record'
import * as S from '@kapitalist/utils/Set'
import type {TChangeEmailResponse} from '~/api/endpoints/account/ChangeEmail'
import type {TChangePasswordResponse} from '~/api/endpoints/account/ChangePassword'
import type {TChangeUsernameResponse} from '~/api/endpoints/account/ChangeUsername'
import LabeledLineTextInput from '~/components/FormFields/LabeledLineTextInput'
import ProvideFormIncremental from '~/components/ProvideForm/incremental'
import ProvideForm from '~/components/ProvideForm/normal'
import type {
  IBasicStringConstraint,
  TTrueOrFalse,
} from '~/components/ProvideForm/utils'
import {notEmpty, TrueOrFalse} from '~/components/ProvideForm/utils'
import {FormRadioButtonGroup, RadioButton} from '~/components/RadioButtonGroup'
import {FormToggle, ToggleForGroup} from '~/components/Toggle'
import type {IToggleGroupRenderProps} from '~/components/ToggleGroup'
import {FormToggleGroup} from '~/components/ToggleGroup'
import DSButton from '~/design-system/Button'
import DSText from '~/design-system/DSText'
import type {TSkin} from '~/game/skins'
import {skinsHuman} from '~/game/skins'
import {
  BirthdayCard,
  JustSayNoCard,
  PassGoCard,
} from '~/models/game/Cards/ActionCard'
import DualPropertyCard from '~/models/game/Cards/DualPropertyCard'
import StandardPropertyCard from '~/models/game/Cards/StandardPropertyCard'
import {passwordConstraints} from '~/pages/Auth/utils'
import CardImage from '~/pages/GameLobby/GameCard/stateless'
import type {TGameLobbySize} from '~/pages/GameLobby/types'
import {gameLobbySizeHuman} from '~/pages/GameLobby/types'
import type {TBetaFeatureSettings} from '~/redux/reducers/settings'
import type {
  TUserNotificationPerSettingOptions,
  TUserNotificationSettings,
} from '~/utils/UserNotifications'
import {
  allUserNotificationPerSettingOptionsFalse,
  humanPerSettingOptions,
  humanSettingDescriptions,
} from '~/utils/UserNotifications'
import styles from './styles.module.css'

// The auto-saving preference fields
type TPreferencesFormData =
  // each string is a JSON'd Set<keyof TUserNotificationPerSettingOptions>
  Record<keyof TUserNotificationSettings, string> &
    // TTrueOrFalse
    Record<keyof TBetaFeatureSettings, string> & {
      gameLobbySize: string // JSON'd Set<TGameLobbySize>
      skin: string // JSON'd Set<TSkin>
    } & Record<'darkMode', TTrueOrFalse>

interface IProps {
  email: string
  username: string
  changeEmail: (email: string) => Promise<TChangeEmailResponse>
  changeUsername: (username: string) => Promise<TChangeUsernameResponse>
  changePassword: (params: {
    currentPassword: string
    newPassword: string
  }) => Promise<TChangePasswordResponse>
  showSuccessToast: (message: string) => void

  gameLobbySize: TGameLobbySize
  setGameLobbySize: (gameLobbySize: TGameLobbySize) => Promise<void>

  skin: TSkin
  setSkin: (skin: TSkin) => Promise<void>

  notifications: TUserNotificationSettings
  setNotificationSetting: (
    setting: keyof TUserNotificationSettings,
    subsettings: TUserNotificationPerSettingOptions
  ) => Promise<void>

  betaFeatures: TBetaFeatureSettings
  setBetaFeatureSetting: (
    setting: keyof TBetaFeatureSettings,
    value: boolean
  ) => Promise<void>

  // NB: from redux (so it's a live value, not stale from bootstrap)
  darkMode: boolean
  setDarkMode: (value: boolean) => Promise<void>
}

interface IState {
  // Bumped to remount/clear the password form after a successful change.
  passwordFormKey: number
}

// Hidden while there's only one skin, but reappears automatically once a second ships.
const hasMultipleSkins = Object.keys(skinsHuman).length > 1

export default class SettingsUI extends React.Component<IProps, IState> {
  state: IState = {
    passwordFormKey: 0,
  }

  render() {
    return (
      <div className={styles.settingsPage}>
        <DSText tag="h1" size="h2" className={styles.pageTitle}>
          Settings
        </DSText>

        {this.renderAccount()}
        {this.renderPreferences()}
      </div>
    )
  }

  renderAccount() {
    return (
      <section className={styles.card}>
        <DSText
          tag="h2"
          size="section-title"
          color="grey"
          className={styles.cardHeading}
        >
          Account
        </DSText>

        <div className={styles.accountPanes}>
          <div className={styles.accountPaneLeft}>
            <DSText size="body-15-demi" className={styles.fieldLabel}>
              Login info
            </DSText>
            <ProvideForm<{email: string}, TChangeEmailResponse>
              onSubmit={form => this.props.changeEmail(form.email.trim())}
              submitSuccess={() => this.props.showSuccessToast('Email saved.')}
              toFormFields={(generateFormFieldProps, formProps) => (
                <div className={styles.accountRow}>
                  <div className={styles.accountInputs}>
                    <LabeledLineTextInput
                      label="Email (for Gravatar)"
                      autoComplete="email"
                      placeholder=" "
                      formFieldProps={generateFormFieldProps('email', [notEmpty], {
                        initialValue: this.props.email,
                      })}
                    />
                  </div>
                  {this.renderSubmitButton('Save', formProps)}
                </div>
              )}
            />

            <ProvideForm<{username: string}, TChangeUsernameResponse>
              onSubmit={form => this.props.changeUsername(form.username.trim())}
              submitSuccess={() => this.props.showSuccessToast('Username saved.')}
              toFormFields={(generateFormFieldProps, formProps) => (
                <div className={styles.accountRow}>
                  <div className={styles.accountInputs}>
                    <LabeledLineTextInput
                      label="Username (what other players see)"
                      autoComplete="username"
                      placeholder=" "
                      formFieldProps={generateFormFieldProps(
                        'username',
                        [notEmpty],
                        {
                          initialValue: this.props.username,
                        }
                      )}
                    />
                  </div>
                  {this.renderSubmitButton('Save', formProps)}
                </div>
              )}
            />
          </div>

          <div className={styles.accountPaneRight}>
            <DSText size="body-15-demi" className={styles.fieldLabel}>
              Change password
            </DSText>

            <ProvideForm<
              {currentPassword: string; newPassword: string},
              TChangePasswordResponse
            >
              key={this.state.passwordFormKey}
              onSubmit={form =>
                this.props.changePassword({
                  currentPassword: form.currentPassword,
                  newPassword: form.newPassword,
                })
              }
              submitSuccess={() => {
                this.props.showSuccessToast('Password updated.')
                this.setState(prev => ({
                  passwordFormKey: prev.passwordFormKey + 1,
                }))
              }}
              toFormFields={(generateFormFieldProps, formProps) => {
                const currentPasswordFFPs = generateFormFieldProps(
                  'currentPassword',
                  [notEmpty]
                )

                return (
                  <div className={styles.accountRow}>
                    <div className={styles.accountInputs}>
                      <LabeledLineTextInput
                        label="Current password"
                        autoComplete="current-password"
                        placeholder=" "
                        inputType="password"
                        onChange={() => formProps.runAllConstraints(false)}
                        formFieldProps={currentPasswordFFPs}
                      />
                      <LabeledLineTextInput
                        label="New password"
                        autoComplete="new-password"
                        placeholder=" "
                        inputType="password"
                        formFieldProps={generateFormFieldProps('newPassword', [
                          ...passwordConstraints,
                          newPasswordMustDifferConstraint(currentPasswordFFPs.value),
                        ])}
                      />
                    </div>
                    {this.renderSubmitButton('Update', formProps)}
                  </div>
                )
              }}
            />
          </div>
        </div>
      </section>
    )
  }

  renderSubmitButton(
    label: string,
    formProps: {isFormSubmitting: boolean; anyErrors: boolean; onSubmit: () => void}
  ) {
    return (
      <DSButton
        className={styles.accountButton}
        buttonStyle="primary"
        size="15px"
        onClick={() => formProps.onSubmit()}
        loading={formProps.isFormSubmitting}
        disabled={formProps.isFormSubmitting}
        appearDisabled={formProps.anyErrors}
      >
        {label}
      </DSButton>
    )
  }

  renderPreferences() {
    return (
      <ProvideFormIncremental<TPreferencesFormData, void>
        wrapperClassName={styles.preferencesWrapper}
        onSubmit={(fieldName, value) => {
          switch (fieldName) {
            case 'gameLobbySize':
              return this.props.setGameLobbySize(
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                FormToggleGroup.formValueToFirstSelection<TGameLobbySize>(value)!
              )
            case 'skin':
              return this.props.setSkin(
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                FormToggleGroup.formValueToFirstSelection<TSkin>(value)!
              )
            case 'gameLog':
              return this.props.setBetaFeatureSetting(
                fieldName,
                TrueOrFalse.toBool(value as TTrueOrFalse)
              )
            case 'darkMode':
              return this.props.setDarkMode(
                TrueOrFalse.toBool(value as TTrueOrFalse)
              )
            default:
              if (S.has(fieldName, recordKeySet(humanSettingDescriptions))) {
                const enabledSubsettings =
                  FormToggleGroup.formValueToSet<
                    keyof TUserNotificationPerSettingOptions
                  >(value)
                const enabledSubsettingsObj = S.reduce(
                  (acc, v) => ({...acc, [v]: true}),
                  enabledSubsettings,
                  {}
                )
                return this.props.setNotificationSetting(fieldName, {
                  ...allUserNotificationPerSettingOptionsFalse,
                  ...enabledSubsettingsObj,
                })
              } else {
                return unreachableCase(fieldName)
              }
          }
        }}
        toFormFields={(generateFormFieldProps, formProps) => (
          <>
            <div className={styles.cardGrid}>
              {this.renderGameCard(generateFormFieldProps, formProps)}
              {this.renderBetaCard(generateFormFieldProps)}
            </div>
            {this.renderNotificationsCard(generateFormFieldProps)}
          </>
        )}
      />
    )
  }

  renderGameCard: TIncrementalRenderer = (generateFormFieldProps, formProps) => (
    <section className={styles.card}>
      <DSText
        tag="h2"
        size="section-title"
        color="grey"
        className={styles.cardHeading}
      >
        Game
      </DSText>

      <DSText size="body-15-demi" className={styles.fieldLabel}>
        Default lobby size
      </DSText>
      <div className={styles.radioList}>
        <FormRadioButtonGroup<TGameLobbySize, TPreferencesFormData>
          allChoices={recordKeySet(gameLobbySizeHuman)}
          initialChoice={this.props.gameLobbySize}
          generateFormFieldProps={generateFormFieldProps}
          formProps={formProps}
          fieldName="gameLobbySize"
          humanChoices={gameLobbySizeHuman}
          blurOnChange
          renderRadioButton={radioButtonProps => (
            <RadioButton
              key={radioButtonProps.choice}
              {...radioButtonProps}
              toggleStyle="radio"
              classNames={{
                choice: styles.radioChoice,
                selectedChoice: styles.selectedRadioChoice,
                toggle: styles.radioToggle,
                fadedToggle: styles.fadedToggle,
                choiceLabel: styles.radioLabel,
              }}
              label={{
                size: 'body-15',
                fadedColor: 'pearl-grey-darkest',
              }}
            />
          )}
        />
      </div>

      {/* TODO if/when there's more than one skin again, this UI might need reconsideration. */}
      {hasMultipleSkins && (
        <>
          <DSText size="body-15-demi" className={styles.fieldLabel}>
            Card skin
          </DSText>
          <div className={styles.radioList}>
            <FormRadioButtonGroup<TSkin, TPreferencesFormData>
              allChoices={recordKeySet(skinsHuman)}
              initialChoice={this.props.skin}
              generateFormFieldProps={generateFormFieldProps}
              formProps={formProps}
              fieldName="skin"
              humanChoices={skinsHuman}
              blurOnChange
              renderRadioButton={radioButtonProps => (
                <RadioButton
                  key={radioButtonProps.choice}
                  {...radioButtonProps}
                  toggleStyle="radio"
                  classNames={{
                    choice: styles.radioChoice,
                    selectedChoice: styles.selectedRadioChoice,
                    toggle: styles.radioToggle,
                    fadedToggle: styles.fadedToggle,
                    choiceLabel: styles.radioLabel,
                  }}
                  label={{
                    size: 'body-15',
                    fadedColor: 'pearl-grey-darkest',
                  }}
                />
              )}
            />
          </div>
          {this.renderSkinPreviews()}
        </>
      )}
    </section>
  )

  renderSkinPreviews() {
    return (
      <div className={styles.skinPreviews}>
        <CardImage
          model={
            new JustSayNoCard({id: 'justSayNo#0', type: 'justSayNo', version: 0})
          }
          invert={false}
          skin={this.props.skin}
          className={styles.cardPreview}
        />
        <CardImage
          model={new PassGoCard({id: 'passGo#0', type: 'passGo', version: 0})}
          invert={false}
          skin={this.props.skin}
          className={styles.cardPreview}
        />
        <CardImage
          model={
            new DualPropertyCard({
              id: 'wildUtilityAndRailroad#0',
              type: 'wildUtilityAndRailroad',
              version: 0,
            })
          }
          invert={true}
          skin={this.props.skin}
          className={styles.cardPreview}
        />
        <CardImage
          model={new StandardPropertyCard({id: 'red#0', type: 'red', version: 0})}
          invert={false}
          skin={this.props.skin}
          className={styles.cardPreview}
        />
        <CardImage
          model={new BirthdayCard({id: 'birthday#0', type: 'birthday', version: 0})}
          invert={false}
          skin={this.props.skin}
          className={styles.cardPreview}
        />
      </div>
    )
  }

  renderBetaCard: TIncrementalRendererNoFormProps = generateFormFieldProps => (
    <section className={styles.card}>
      <DSText
        tag="h2"
        size="section-title"
        color="grey"
        className={styles.cardHeading}
      >
        Beta
      </DSText>

      <div className={styles.toggleStack}>
        <FormToggle<TTrueOrFalse, typeof TrueOrFalse>
          formFieldProps={generateFormFieldProps('darkMode', [], {
            initialValue: this.props.darkMode.toString(),
          })}
          booleanString={TrueOrFalse}
          toggleStyle="checkbox"
          blurOnChange
        >
          {input => (
            <span className={styles.betaFeatureInputAndLabel}>
              {input}
              <DSText size="body-15">Dark mode</DSText>
            </span>
          )}
        </FormToggle>

        <FormToggle<TTrueOrFalse, typeof TrueOrFalse>
          formFieldProps={generateFormFieldProps('gameLog', [], {
            initialValue: TrueOrFalse.toString(this.props.betaFeatures.gameLog),
          })}
          booleanString={TrueOrFalse}
          toggleStyle="checkbox"
          blurOnChange
        >
          {input => (
            <span className={styles.betaFeatureInputAndLabel}>
              {input}
              <DSText size="body-15">Game log</DSText>
            </span>
          )}
        </FormToggle>
      </div>
    </section>
  )

  renderNotificationsCard: TIncrementalRendererNoFormProps =
    generateFormFieldProps => (
      <section className={styles.card}>
        <DSText
          tag="h2"
          size="section-title"
          color="grey"
          className={styles.cardHeading}
        >
          Notifications
        </DSText>

        <table className={styles.notificationsTable}>
          <thead>
            <tr>
              <th />
              {M.mapEntries(
                (subsettingHuman, key) => (
                  <DSText
                    tag="th"
                    size="section-title"
                    color="grey-dark"
                    key={key + 'header'}
                  >
                    {subsettingHuman}
                  </DSText>
                ),
                M.fromRecord(humanPerSettingOptions)
              )}
            </tr>
          </thead>
          <tbody>
            {M.mapEntries((settingHuman, settingKey) => {
              return (
                <tr key={settingKey}>
                  <DSText tag="td" size="body-15" className={styles.notifTableLabel}>
                    {settingHuman}
                  </DSText>
                  <FormToggleGroup<keyof TUserNotificationPerSettingOptions>
                    allowDeselection
                    blurOnChange
                    selectionLimit={undefined}
                    formFieldProps={generateFormFieldProps(settingKey, [], {
                      initialValue: FormToggleGroup.setToFormValue(
                        truthyKeys(this.props.notifications[settingKey])
                      ),
                    })}
                  >
                    {(
                      groupProps: IToggleGroupRenderProps<
                        keyof TUserNotificationPerSettingOptions
                      >
                    ) =>
                      M.mapEntries(
                        (subsettingHuman, subsettingKey) => (
                          <td
                            key={subsettingKey}
                            className={styles.notifTableToggle}
                          >
                            <ToggleForGroup<keyof TUserNotificationPerSettingOptions>
                              toggleValue={subsettingKey}
                              groupProps={groupProps}
                              toggleStyle="checkbox"
                              wrapperClassName={styles.subsettingWrapper}
                            />
                          </td>
                        ),
                        M.fromRecord(humanPerSettingOptions)
                      )
                    }
                  </FormToggleGroup>
                </tr>
              )
            }, M.fromRecord(humanSettingDescriptions))}
          </tbody>
        </table>
      </section>
    )
}

type TIncrementalToFormFields = React.ComponentProps<
  typeof ProvideFormIncremental<TPreferencesFormData, void>
>['toFormFields']
type TIncrementalRenderer = (
  generateFormFieldProps: Parameters<TIncrementalToFormFields>[0],
  formProps: Parameters<TIncrementalToFormFields>[1]
) => React.ReactNode
type TIncrementalRendererNoFormProps = (
  generateFormFieldProps: Parameters<TIncrementalToFormFields>[0]
) => React.ReactNode

const truthyKeys = <T extends string>(record: Record<T, boolean>): Set<T> => {
  return M.keySet(M.filter(v => !!v, M.fromRecord(record)))
}

const newPasswordMustDifferConstraint = (
  currentPassword: string
): IBasicStringConstraint => ({
  constraint: newPassword => newPassword !== currentPassword,
  message: () => 'Those passwords are the same.',
})
